// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Shared hook for per-bill pipeline triggering with job polling and status tracking.
 *
 * Used by both MarylandBillsBrowser and FederalBillsBrowser.
 */
import { useCallback, useState } from 'react'
import type { NodeApiClient } from '@/app-shell/services'
import { useAgents } from '@/subsystems/agents'
import { triggerAnalyzeBill } from '../services/bills.service'
import { fetchJobStatus } from '@/subsystems/jobs/services/jobs.service'

export type JobTrackingStatus = 'running' | 'completed' | 'error'

export interface UsePipelineJobTrackerOptions {
  apiClient: NodeApiClient
  orgId: string
  workspaceId: string
  /** Called when a score pipeline job completes with the bill number (doc ID) */
  onScoreJobComplete?: (docId: string) => void
  /** Called when a non-score pipeline job completes with the bill number (doc ID) */
  onReviewJobComplete?: (docId: string) => void
}

export interface UsePipelineJobTrackerResult {
  /** Map of bill key -> job status */
  statuses: Map<string, JobTrackingStatus>
  /** Trigger a pipeline for a bill, then poll until done */
  runPipeline: (params: {
    /** Key used for status tracking (e.g. bill number or "type-number") */
    billKey: string
    /** Bill number sent to the API */
    billNumber: string
    /** Session code sent to the API */
    sessionCode: string
    /** Pipeline to run */
    pipelineId: string
    /** Optional version for review pipelines */
    version?: number
  }) => void
}

export function usePipelineJobTracker({
  apiClient,
  orgId,
  workspaceId,
  onScoreJobComplete,
  onReviewJobComplete,
}: UsePipelineJobTrackerOptions): UsePipelineJobTrackerResult {
  const [statuses, setStatuses] = useState<Map<string, JobTrackingStatus>>(new Map())
  const { data: agents = [] } = useAgents(apiClient, orgId, workspaceId)

  const runPipeline = useCallback(
    async (params: {
      billKey: string
      billNumber: string
      sessionCode: string
      pipelineId: string
      version?: number
    }) => {
      const { billKey, billNumber, sessionCode, pipelineId, version } = params
      const isScorePipeline = pipelineId.startsWith('score-')
      // Agent-owned pipelines resolve under their agent; find the owner by pipeline id.
      const agentId = agents.find(a => a.pipelineId === pipelineId)?.agentId ?? null

      setStatuses(prev => new Map(prev).set(billKey, 'running'))
      try {
        const response = await triggerAnalyzeBill(
          apiClient, orgId, workspaceId, pipelineId, agentId, billNumber, sessionCode, version,
        )

        const poll = async () => {
          try {
            const status = await fetchJobStatus(apiClient, orgId, workspaceId, response.jobId)
            if (status.status === 'COMPLETED') {
              setStatuses(prev => new Map(prev).set(billKey, 'completed'))
              if (isScorePipeline) {
                onScoreJobComplete?.(billNumber)
              } else {
                onReviewJobComplete?.(billNumber)
              }
            } else if (status.status === 'ERROR') {
              setStatuses(prev => new Map(prev).set(billKey, 'error'))
            } else {
              setTimeout(poll, 3000)
            }
          } catch {
            setStatuses(prev => new Map(prev).set(billKey, 'error'))
          }
        }
        setTimeout(poll, 2000)
      } catch {
        setStatuses(prev => new Map(prev).set(billKey, 'error'))
      }
    },
    [apiClient, orgId, workspaceId, agents, onScoreJobComplete, onReviewJobComplete],
  )

  return { statuses, runPipeline }
}
